import { ClickoutUrl } from '@/helpers/clickout-helper';
import { useExperiment } from '@/libraries/experiment';
import { getExperimentAffiliateTag } from '@/libraries/experiment/experiment-affiliation';
import { useLanguageStore } from '@/stores/pinia/useLanguageStore';
import { storeToRefs } from 'pinia';
import { computed } from 'vue';

export function usePrimePlayBtnExp() {
	const { country, language } = storeToRefs(useLanguageStore());
	// We need to use global, because this experiment is activated on titles pages, but rendered on a modal
	const {
		activeVariantWithControlGroup: variant,
		experimentInfo,
		trackEvent,
	} = useExperiment('EXP_PRIME_PLAY_BTN', 'global');

	const isPrimePlayBtnVariant = computed(() => !!variant.value && variant.value !== 'control');

	const primePlayBtnShortName = computed(() => 'amp');

	function setPrimePlayBtnParams(
		url: ClickoutUrl,
		placement: 'qtv' | 'ft' | 'cta',
		shortName: string,
		inline?: boolean
	) {
		if (!variant.value || inline) return url;

		const affiliationShortId = experimentInfo.value.affiliationShortId as string;

		const tag = getExperimentAffiliateTag({
			affiliationShortId,
			variant: variant.value,
			country: country.value,
			placement,
			providerShortName: shortName,
		});

		url.set('uct_ctag', tag);

		return url;
	}

	/* Experiment Translatables */
	const isDE = computed(() => language.value === 'de');

	const adLabel = computed(() => (isDE.value ? 'Anzeige' : 'Ad'));
	const skipLabel = computed(() => (isDE.value ? 'Überspringen' : 'Skip'));

	function skipInLabel(seconds: number) {
		return isDE.value ? `Überspringen in ${seconds}s` : `Skip in ${seconds}s`;
	}
	function taglineV1(title: string) {
		return isDE.value
			? `Wird Fans von ${title} auch gefallen...`
			: `Streamers who watched ${title} also watched...`;
	}
	function taglineV2(title: string, provider: string) {
		return isDE.value
			? `Ähnliche Titel wie ${title} gratis bei ${provider}!`
			: `Stream titles similar to ${title} on ${provider} for free!`;
	}
	function similarToLabel(title: string) {
		return isDE.value ? `Ähnlich wie ${title}` : `Similar to ${title}`;
	}
	function priceLabel(price: string) {
		const days = primePlayBtnShortName.value === 'amp' ? '30' : '7';
		return isDE.value ? `${days} Tage gratis, danach ${price} / Monat` : `${days} Days Free, Then ${price} / month`;
	}

	const experimentStrings = { adLabel, skipLabel, skipInLabel, taglineV1, taglineV2, similarToLabel, priceLabel };

	return {
		variant,
		isPrimePlayBtnVariant,
		primePlayBtnShortName,
		setPrimePlayBtnParams,
		experimentStrings,
		trackEvent,
	};
}
